import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  input,
  effect,
  output,
  inject,
} from '@angular/core';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { TreeStepCardComponent } from './tree-step-card/tree-step-card.component';
import { OverlayLoaderComponent } from '../overlay-loader/overlay-loader.component';
import moment from 'moment';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject, takeUntil, timer } from 'rxjs';
import { TreeActionsComponent } from './tree-actions/tree-actions.component';
import { StepsDiagramPosition } from '../../models/steps-diagram.model';
import { TreeTag, TreeTagsComponent } from './tree-tags/tree-tags.component';

@Component({
  selector: 'lib-tree',
  standalone: true,
  imports: [
    TreeStepCardComponent,
    OverlayLoaderComponent,
    TranslocoPipe,
    TreeActionsComponent,
    TreeTagsComponent,
  ],
  templateUrl: './tree.component.html',
  styleUrl: './tree.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TreeComponent implements OnInit, OnDestroy {
  positions = input.required<StepsDiagramPosition[]>();
  requestName = input<string>('1. trigger');
  active = input.required<boolean>();
  showActions = input<boolean>(true);
  showTags = input<boolean>(true);
  tags = input<TreeTag[]>([]);
  tagsChanged = output<string>();
  treeContainerClass = input<string>('');
  zoom = signal<number>(1);
  isMaximized = signal<boolean>(false);
  isContentSmallerThanContainer = signal(false);
  maxZoom = signal(1.41);
  minZoom = signal(0.11);
  containerHeight = signal<string>('calc(100vh - 25rem)');
  pdfLoading = signal<boolean>(false);
  private destroy$ = new Subject<void>();

  translateX = signal<number>(0);
  translateY = signal<number>(0);

  isDragging = signal<boolean>(false);
  startX = signal<number>(0);
  startY = signal<number>(0);
  initialTranslateX = signal<number>(0);
  initialTranslateY = signal<number>(0);
  scrollLeft = signal<number>(0);
  scrollTop = signal<number>(0);
  scale = signal<number>(1);
  constructor() {
    effect(() => {
      if (this.active()) {
        this.delayInitZoomOut();
      }
    });
  }

  ngOnInit(): void {
    window.addEventListener('resize', () => this.initZoomOut());
    this.delayInitZoomOut();
  }

  onTagsChanged(value: string) {
    this.tagsChanged.emit(value);
  }

  initZoomOut() {
    const flowStepsContainer = document.getElementById('flow-steps-container');
    if (!flowStepsContainer) {
      this.zoom.set(1);
      return;
    }

    const longestBranch = this.positions().reduce((max, step) => {
      return Math.max(max, step.steps.length || 0);
    }, 0);
    if (longestBranch === 0) {
      this.zoom.set(1);
      return;
    }

    const minZoom = Math.min(
      this.getZoomBasedOnWidth(flowStepsContainer),
      this.getZoomBasedOnHeight(flowStepsContainer)
    );
    this.zoom.set(minZoom);
  }

  getZoomBasedOnWidth(flowStepsContainer: HTMLElement): number {
    const containerWidth = flowStepsContainer.clientWidth;
    const containerPadding = 24; // p-6 padding
    const availableWidth = containerWidth - containerPadding;

    const positionCount =
      this.positions().length > 5
        ? this.positions().length
        : this.positions().length + 1;
    const stepWidth = 321;
    const stepGap = 80;
    const requiredWidth =
      positionCount * stepWidth + (positionCount - 1) * stepGap;

    /* Calculate zoom to fit the content */
    const calculatedZoom = availableWidth / requiredWidth;

    /* Set zoom with reasonable limits */
    return Math.max(this.minZoom(), Math.min(1, calculatedZoom));
  }

  getZoomBasedOnHeight(flowStepsContainer: HTMLElement): number {
    const containerHeight = flowStepsContainer.clientHeight;
    const containerPadding = 48;
    const availableHeight = containerHeight - containerPadding;

    const longestBranch = this.positions().reduce((max, step) => {
      return Math.max(max, step.steps.length || 0);
    }, 1);
    const branchHeight = 136;
    const branchGap = 80;
    const requiredHeight =
      longestBranch * branchHeight + (longestBranch - 1) * branchGap;

    const calculatedZoom = availableHeight / requiredHeight;

    return Math.min(this.maxZoom(), calculatedZoom);
  }

  exportPDF() {
    this.pdfLoading.set(true);
    const stepContainer = document.getElementById('step-container');

    if (!stepContainer) {
      this.pdfLoading.set(false);
      return;
    }

    const tempStepContainer = this.prepareExportContainer(stepContainer);
    const { contentWidth, contentHeight } =
      this.getContentDimensions(tempStepContainer);
    const dpiScale = 120 / 96;

    html2canvas(tempStepContainer, {
      scale: dpiScale,
      width: contentWidth,
      height: contentHeight,
      backgroundColor: '#fff',
      useCORS: true,
      logging: false,
      removeContainer: true,
    })
      .then(canvas => this.prepareExport(canvas, contentWidth, contentHeight))
      .finally(() => this.cleanupExport(tempStepContainer));
  }

  private prepareExport(
    canvas: HTMLCanvasElement,
    contentWidth: number,
    contentHeight: number
  ) {
    const imgData = canvas.toDataURL('image/png');
    const orientation = this.getPdfOrientation(contentWidth, contentHeight);
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format: 'a4',
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const marginMM = 24 * 0.264583; // (1px = 0.264583 mm)
    const usableWidth = pdfWidth - 2 * marginMM;
    const usableHeight = pdfHeight - 2 * marginMM;
    const { imgWidth, imgHeight, x, y } = this.calculateImageSizeAndPosition(
      contentWidth,
      contentHeight,
      usableWidth,
      usableHeight,
      pdfWidth,
      pdfHeight
    );
    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
    const date = new Date();
    const formattedDate = moment(date).format('YYYY_MM_DD_HHmmss');
    pdf.save(`${formattedDate}_${this.requestName()}_steps_diagram.pdf`);
  }

  private cleanupExport(tempStepContainer: HTMLElement) {
    if (this.isMaximized()) {
      this.maximize();
    }
    this.cleanupExportContainer(tempStepContainer);
    this.pdfLoading.set(false);
  }

  private prepareExportContainer(stepContainer: HTMLElement): HTMLElement {
    const tempWrapper = document.createElement('div');
    tempWrapper.style.position = 'absolute';
    tempWrapper.style.left = '-99999px';
    tempWrapper.style.top = '0';
    tempWrapper.style.background = '#fff';
    tempWrapper.style.width = `${stepContainer.offsetWidth}px`;
    tempWrapper.style.height = `${stepContainer.offsetHeight}px`;
    tempWrapper.appendChild(stepContainer.cloneNode(true));
    document.body.appendChild(tempWrapper);
    const tempStepContainer = tempWrapper.firstElementChild as HTMLElement;
    tempStepContainer.style.transform = 'none';
    tempStepContainer.style.padding = '0';
    tempStepContainer.style.margin = '0';
    tempStepContainer.style.background = '#fff';
    tempStepContainer.style.width = `${stepContainer.offsetWidth}px`;
    tempStepContainer.style.height = `${stepContainer.offsetHeight}px`;
    return tempStepContainer;
  }

  private getContentDimensions(container: HTMLElement): {
    contentWidth: number;
    contentHeight: number;
  } {
    const rect = container.getBoundingClientRect();
    return {
      contentWidth: Math.ceil(rect.width),
      contentHeight: Math.ceil(rect.height),
    };
  }

  private getPdfOrientation(
    contentWidth: number,
    contentHeight: number
  ): 'portrait' | 'landscape' {
    return contentWidth > contentHeight ? 'landscape' : 'portrait';
  }

  private calculateImageSizeAndPosition(
    contentWidth: number,
    contentHeight: number,
    usableWidth: number,
    usableHeight: number,
    pdfWidth: number,
    pdfHeight: number
  ): { imgWidth: number; imgHeight: number; x: number; y: number } {
    let imgWidth = usableWidth;
    let imgHeight = (contentHeight / contentWidth) * usableWidth;
    if (imgHeight > usableHeight) {
      imgHeight = usableHeight;
      imgWidth = (contentWidth / contentHeight) * usableHeight;
    }
    const x = (pdfWidth - imgWidth) / 2;
    const y = (pdfHeight - imgHeight) / 2;
    return { imgWidth, imgHeight, x, y };
  }

  private cleanupExportContainer(tempStepContainer: HTMLElement) {
    const tempWrapper = tempStepContainer.parentNode;
    if (tempWrapper && tempWrapper.parentNode) {
      tempWrapper.parentNode.removeChild(tempWrapper);
    }
  }

  zoomIn() {
    if (this.zoom() < this.maxZoom()) {
      this.zoom.set(this.zoom() + 0.1);
    }
  }

  zoomOut() {
    if (this.zoom() > this.minZoom()) {
      this.zoom.set(this.zoom() - 0.1);
    }
  }

  maximize() {
    const sidenav = document.querySelector('lib-sidenav');
    const requestDetailsHeader = document.querySelector(
      'lib-request-details-header'
    );
    const navTabs = document.querySelector('.nav-tabs');
    const pageContainer = document.querySelector('.page-container');
    const stepContainer = document.querySelector(
      '#step-container'
    ) as HTMLElement;

    if (
      !sidenav ||
      !requestDetailsHeader ||
      !navTabs ||
      !pageContainer ||
      !stepContainer
    ) {
      this.isMaximized.set(false);
      this.containerHeight.set('calc(100vh - 25rem)');
      return;
    }

    this.isMaximized.set(!this.isMaximized());
    if (this.isMaximized()) {
      sidenav.classList.add('hidden');
      requestDetailsHeader.classList.add('hidden');
      navTabs.classList.add('hidden');
      pageContainer.classList.remove('ms-5');
      this.containerHeight.set('calc(100vh - 9.25rem)');
    } else {
      sidenav.classList.remove('hidden');
      requestDetailsHeader.classList.remove('hidden');
      navTabs.classList.remove('hidden');
      pageContainer.classList.add('ms-5');
      this.containerHeight.set('calc(100vh - 25rem)');
    }

    this.delayInitZoomOut();
  }

  delayInitZoomOut() {
    timer(100)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initZoomOut();
      });
  }

  onPointerDown(event: PointerEvent) {
    const target = event.target as HTMLElement;

    this.isDragging.set(true);
    this.startX.set(event.clientX);
    this.startY.set(event.clientY);
    this.initialTranslateX.set(this.translateX());
    this.initialTranslateY.set(this.translateY());

    target.setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.isDragging()) return;

    event.preventDefault();

    const deltaX = event.clientX - this.startX();
    const deltaY = event.clientY - this.startY();

    this.translateX.set(this.initialTranslateX() + deltaX);
    this.translateY.set(this.initialTranslateY() + deltaY);
  }

  onPointerUp(event: PointerEvent) {
    if (!this.isDragging()) return;

    this.isDragging.set(false);

    const target = event.target as HTMLElement;
    target.releasePointerCapture(event.pointerId);
  }

  onPointerLeave(event: PointerEvent) {
    if (this.isDragging()) {
      this.onPointerUp(event);
    }
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();

    const deltaX = event.deltaX * 0.5;
    const deltaY = event.deltaY * 0.5;

    this.translateX.set(this.translateX() - deltaX);
    this.translateY.set(this.translateY() - deltaY);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.initZoomOut);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
